/**
 * 编辑回忆（design §4.7）：表单里的四个字段，和打开编辑时看到的版本比改了哪些，撞上 Ta 刚改过时怎么合并。
 * 只有数据，不碰界面，方便测。
 *
 * 两个人可能同时在改同一段回忆。服务端拿 base（打开编辑时看到的 updatedAt）判断，这期间有人改过就退回 409 和最新的版本。
 * 这里按字段三方比较：我改的字段 Ta 没动（或者改成了一样的），就在最新版本上直接重试；
 * 我和 Ta 改了同一个字段、改得还不一样，才停下来给人看 Ta 写了什么，由人决定用谁的。
 */
import type { MemoryDto, MemoryPatch, Shine } from '@star-sea/shared';

export interface MemoryFields {
  title: string;
  place: string;
  story: string;
  shine: Shine;
}

export type FieldName = keyof MemoryFields;
export type FieldChanges = Partial<MemoryFields>;

export const FIELD_NAMES: readonly FieldName[] = ['title', 'place', 'story', 'shine'];
export const FIELD_LABELS: Readonly<Record<FieldName, string>> = { title: '标题', place: '地点', story: '故事', shine: '心动' };

/** 一行字（标题、地点）：换行和连续的空白并成一个空格。和服务端（app.ts 的 line）一样 */
function oneLine(s: string): string {
  return s.normalize('NFC').replace(/[\s\u0000-\u001f\u007f]+/g, ' ').trim();
}

/** 一段字（故事）：换行统一成 \n，去掉别的控制字符。和服务端（app.ts 的 passage）一样 */
function passage(s: string): string {
  return s
    .normalize('NFC')
    .replace(/\r\n?/g, '\n')
    .replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '')
    .trim();
}

/** 按服务端存的样子整理一遍：比较「改没改」时，多打的空格、Windows 的换行不算改动 */
export function tidy(f: MemoryFields): MemoryFields {
  return { title: oneLine(f.title), place: oneLine(f.place), story: passage(f.story), shine: f.shine };
}

export function fieldsOf(m: MemoryDto): MemoryFields {
  return { title: m.title ?? '', place: m.place?.name ?? '', story: m.story ?? '', shine: m.shine };
}

/** draft 比 base 改了哪些字段（整理过以后再比）；值是整理过的 */
export function changesOf(base: MemoryFields, draft: MemoryFields): FieldChanges {
  const a = tidy(base), b = tidy(draft), out: FieldChanges = {};
  for (const k of FIELD_NAMES) if (a[k] !== b[k]) Object.assign(out, { [k]: b[k] });
  return out;
}

export const isEmpty = (c: FieldChanges) => FIELD_NAMES.every((k) => c[k] === undefined);

/** 发给服务端的改动：空字符串是清空 */
export function toPatch(base: string, c: FieldChanges): MemoryPatch {
  const p: MemoryPatch = { base };
  if (c.title !== undefined) p.title = c.title || null;
  if (c.story !== undefined) p.story = c.story || null;
  if (c.place !== undefined) p.place = c.place ? { name: c.place } : null;
  if (c.shine !== undefined) p.shine = c.shine;
  return p;
}

export interface Rebased {
  /** 我和 Ta 都改了、而且改得不一样的字段 */
  conflicts: FieldName[];
  /** 在最新的版本上还要改的字段（Ta 已经改成一样的那些不用再发） */
  changes: FieldChanges;
}

/** 撞上 Ta 刚改过：base 是我打开编辑时看到的，mine 是我改了的字段，latest 是服务端现在的 */
export function rebase(base: MemoryFields, mine: FieldChanges, latest: MemoryFields): Rebased {
  const b = tidy(base), l = tidy(latest), conflicts: FieldName[] = [], changes: FieldChanges = {};
  for (const k of FIELD_NAMES) {
    const v = mine[k];
    if (v === undefined || v === l[k]) continue;
    if (l[k] !== b[k]) conflicts.push(k);
    Object.assign(changes, { [k]: v });
  }
  return { conflicts, changes };
}

/** 提示里说 Ta 改了什么：「给 2024 年 5 月 20 日写了故事」「改了 … 的标题、故事」；什么都没变返回 null */
export function editNote(who: string, day: string, before: MemoryFields | null, after: MemoryFields): string | null {
  const changed = before ? FIELD_NAMES.filter((k) => tidy(before)[k] !== tidy(after)[k]) : FIELD_NAMES.filter((k) => k !== 'shine' && after[k]);
  if (!changed.length) return null;
  if (changed.length === 1 && changed[0] === 'story' && !before?.story && after.story) return `${who} 给 ${day}写了故事`;
  if (changed.length === 1 && changed[0] === 'shine') return `${who} 把 ${day}的心动改成了 ${after.shine}`;
  return `${who} 改了 ${day}的${changed.map((k) => FIELD_LABELS[k]).join('、')}`;
}
